import { KnowledgeEntity, KnowledgeKind } from "./api";

/**
 * How each kind of accumulated entity is presented. Kept in one place so the
 * browse list, the entity screen and any future surface label the same thing
 * the same way.
 */
type KindPresentation = {
  label: string;
  plural: string;
  icon: string;
};

const KINDS: Record<KnowledgeKind, KindPresentation> = {
  place: { label: "Place", plural: "Places", icon: "fa-location-dot" },
  song: { label: "Song", plural: "Songs", icon: "fa-music" },
  artist: { label: "Artist", plural: "Artists", icon: "fa-microphone-lines" },
  topic: { label: "Topic", plural: "Topics", icon: "fa-hashtag" },
  tag: { label: "Tag", plural: "Tags", icon: "fa-tag" },
  recipe: { label: "Recipe", plural: "Recipes", icon: "fa-utensils" },
  ingredient: { label: "Ingredient", plural: "Ingredients", icon: "fa-carrot" },
  domain: { label: "Source", plural: "Sources", icon: "fa-globe" },
  link: { label: "Link", plural: "Links", icon: "fa-link" },
};

const UNKNOWN_KIND: KindPresentation = { label: "Entity", plural: "Entities", icon: "fa-diagram-project" };

/** Unknown kinds are possible: the server may know a kind this bundle does not. */
export const kindPresentation = (kind: string): KindPresentation =>
  KINDS[kind as KnowledgeKind] ?? { ...UNKNOWN_KIND, label: kind, plural: kind };

/** The order kinds are offered in — most of the library first. */
export const KIND_ORDER: KnowledgeKind[] = [
  "place",
  "song",
  "artist",
  "recipe",
  "ingredient",
  "topic",
  "tag",
  "domain",
];

const asText = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

/**
 * The one line under an entity's name: whatever best identifies this particular
 * thing — an address for a place, the artist for a song — rather than a generic
 * restatement of its kind.
 */
export const entityDetail = (entity: KnowledgeEntity): string | undefined => {
  switch (entity.kind) {
    case "place":
      return asText(entity.props.address) ?? asText(entity.props.category);
    case "song":
      return asText(entity.props.artist) ?? asText(entity.props.album);
    case "recipe": {
      const servings = typeof entity.props.servings === "number" ? `${entity.props.servings} servings` : undefined;
      const time = asText(entity.props.cook_time) ?? asText(entity.props.prep_time);
      return [servings, time].filter(Boolean).join(" · ") || undefined;
    }
    case "link":
      return asText(entity.props.url);
    default:
      return undefined;
  }
};

/** Where this entity lives outside Sieve, when the extraction resolved one. */
export const entityExternalUrl = (entity: KnowledgeEntity): { url: string; label: string } | undefined => {
  const maps = asText(entity.props.google_maps_pin);
  if (maps && maps.startsWith("http")) return { url: maps, label: "Open in Google Maps" };

  const spotify = asText(entity.props.spotify_url);
  if (spotify) return { url: spotify, label: "Open in Spotify" };

  const url = asText(entity.props.url);
  if (url) return { url, label: "Open link" };

  return undefined;
};

export const mentionsLabel = (mentions: number): string =>
  mentions === 1 ? "1 link" : `${mentions} links`;
